import type { SeedIsland } from './seedIslands'

/**
 * The Mandarin half of the phone-and-officialdom expansion the other four
 * languages carry.
 *
 * The phone is the hardest place to be a learner in any language and worse in
 * Mandarin: no lip movement to read, no writing a character in the air, and
 * tones flattened by a bad connection. A block that arrives without assembly is
 * the only thing that works there.
 *
 * Pinyin here is written as spoken, with 一 and 不 sandhi applied, matching the
 * rest of the Mandarin set. seed.test.ts checks every block's syllable count
 * against its character count, so a slip fails the build rather than quietly
 * marking a correct answer wrong for the life of the record.
 */
export const EXTRA_ZH: SeedIsland[] = [
  {
    id: 'isl_dianhua',
    title: 'On the phone',
    titleZh: '打电话',
    useWhen: 'No face to read and no characters to draw. Overlearn the opening.',
    tags: ['transactional', 'phone'],
    hskLevel: 3,
    blocks: [
      [
        '喂，您好，请问是李经理吗？',
        'wéi nín hǎo qǐng wèn shì lǐ jīng lǐ ma',
        'Hello, is that Manager Li?',
        '喂 is the telephone hello and nothing else — using it face to face is like answering a doorbell with "speaking".',
      ],
      [
        '不好意思，我没听清楚，能再说一遍吗？',
        'bù hǎo yì si wǒ méi tīng qīng chu néng zài shuō yí biàn ma',
        "Sorry, I didn't catch that — could you say it again?",
        '听清楚 — hear clearly. 没听清楚 blames your ears rather than their speech, which is the polite direction.',
      ],
      [
        '我想预约一个时间。',
        'wǒ xiǎng yù yuē yí ge shí jiān',
        "I'd like to make an appointment.",
        '预约 is booking ahead — a slot, a doctor, a viewing. 约 alone is arranging to meet a friend.',
      ],
      [
        '麻烦您给我发个邮件，好吗？',
        'má fan nín gěi wǒ fā ge yóu jiàn hǎo ma',
        'Could you send me an email about it?',
        'The move that rescues a call going badly: get it in writing. 好吗 on the end turns an instruction into a request.',
      ],
      [
        '信号不太好，我换个地方再打给您。',
        'xìn hào bú tài hǎo wǒ huàn ge dì fang zài dǎ gěi nín',
        "The signal is poor — I'll move and call you back.",
        '不 takes second tone before a fourth: bú tài, not bù tài. The whole line is also a graceful exit when you need one.',
      ],
      [
        '谢谢您，那就这样，再见。',
        'xiè xie nín nà jiù zhè yàng zài jiàn',
        "Thank you. That's everything, goodbye.",
        '那就这样 — "then it is like this" — is how Mandarin closes a call. Without it, hanging up feels abrupt.',
      ],
    ],
  },
  {
    id: 'isl_banshi',
    title: 'Officialdom',
    titleZh: '办事',
    useWhen: 'Fixed vocabulary, narrow script, and a real cost to fumbling it.',
    tags: ['transactional', 'bureaucracy'],
    hskLevel: 3,
    blocks: [
      [
        '我预约了十点，我姓莫。',
        'wǒ yù yuē le shí diǎn wǒ xìng mò',
        'I have a ten o’clock appointment, my surname is Mo.',
        '姓 is a verb — "I am surnamed". Giving the surname alone is the expected form at a counter.',
      ],
      [
        '请问需要准备哪些材料？',
        'qǐng wèn xū yào zhǔn bèi nǎ xiē cái liào',
        'Which documents do I need to bring?',
        '材料 covers documents, forms and evidence together. 哪些 asks "which ones" of an expected several.',
      ],
      [
        '这个我今天没带，可以补交吗？',
        'zhè ge wǒ jīn tiān méi dài kě yǐ bǔ jiāo ma',
        "I don't have that with me today — can I submit it later?",
        '补交 — submit as a supplement — is the exact word for handing something in afterwards. Asking is routine, not a favour.',
      ],
      [
        '请问接下来怎么办？',
        'qǐng wèn jiē xià lái zěn me bàn',
        'What happens next?',
        '怎么办 is "what is to be done", not "how to do it". One of the most reusable three-character strings in the language.',
      ],
      [
        '表格我已经填好了，还缺什么吗？',
        'biǎo gé wǒ yǐ jīng tián hǎo le hái quē shén me ma',
        'I have filled in the form. Is anything still missing?',
        '填好了 — the 好 marks it as done properly, not merely done. The question is what ends the visit cleanly.',
      ],
      [
        '我刚搬来，想办一下居住登记。',
        'wǒ gāng bān lái xiǎng bàn yí xià jū zhù dēng jì',
        "I've just moved here and want to register my residence.",
        '一 before a fourth tone becomes yí: bàn yí xià. 一下 softens the request to "just briefly".',
      ],
    ],
  },
]
